import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * wide (≥ 1280 px): chat + context column · medium (768–1279 px): chat,
 * context in a drawer · mobile (< 768 px): full-screen chat, context in a drawer.
 */
export type WorkspaceLayout = 'wide' | 'medium' | 'mobile';

export interface WorkspaceChrome {
  layout: WorkspaceLayout;
  contextOpen: boolean;
  onContextOpenChange: (open: boolean) => void;
  /** Phone: back to the list. */
  onBack?: () => void;
}

type Props = WorkspaceChrome & {
  labelledBy: string;
  chat: ReactNode;
  context: ReactNode | null;
};

export function WorkspaceFrame({ layout, contextOpen, onContextOpenChange, labelledBy, chat, context }: Props) {
  return (
    <>
      <section aria-labelledby={labelledBy} className="flex min-h-0 min-w-0 flex-1 flex-col bg-card">
        {chat}
      </section>
      {context && layout === 'wide' && contextOpen && (
        <aside
          aria-label="Kontext"
          className="flex w-[256px] flex-shrink-0 flex-col overflow-y-auto border-l border-border bg-card 2xl:w-[296px]"
        >
          {context}
        </aside>
      )}
      {context && layout !== 'wide' && (
        <Sheet open={contextOpen} onOpenChange={onContextOpenChange}>
          <SheetContent side="right" className="w-[88vw] overflow-y-auto p-0 sm:max-w-sm" aria-describedby={undefined}>
            <SheetTitle className="px-5 pb-1 pt-5 text-base">Kontext</SheetTitle>
            {context}
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="-ml-1 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      aria-label="Zurück zur Liste"
    >
      <ArrowLeft className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}

/** Placeholder while a chat loads; on the phone with the way back already there. */
export function ChatSkeleton({ onBack }: { onBack?: () => void }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col" aria-label="Chat wird geladen">
      <div className="flex items-center gap-3 border-b border-border px-3 py-3 sm:px-5">
        {onBack && <BackButton onBack={onBack} />}
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-64" />
        </div>
      </div>
      <div className="flex-1 space-y-3 bg-chrome/60 p-6">
        <Skeleton className="h-10 w-1/2 rounded-2xl" />
        <Skeleton className="ml-auto h-14 w-2/3 rounded-2xl" />
        <Skeleton className="h-10 w-2/5 rounded-2xl" />
      </div>
    </div>
  );
}

export function ChatError({ message, onRetry, onBack }: { message: string; onRetry?: () => void; onBack?: () => void }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-8 text-center text-sm text-muted-foreground">
      <p>{message}</p>
      <div className="flex gap-2">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            Zurück zur Liste
          </button>
        )}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-brand-foreground hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            Erneut versuchen
          </button>
        )}
      </div>
    </div>
  );
}
